import { useState, useRef, useEffect, useCallback } from "react";
import "../App.css";
import { useSelector } from "react-redux";
import { getApi, postApi, postMultipartApi } from "../store/reducers/Apiservices";
import { store } from "../App";

// ── Helpers ───────────────────────────────────────────────────────────────────
const COLORS = [
  "#c7d9f5","#b5e8d4","#f5d6c8","#d4f0c8","#f5c8d4",
  "#e8d4f5","#f5f0c8","#c8e8f5","#fce4b5","#d5f5c8",
];
let photoIdCounter = 100;

const FOLDERS = [
  { key: "raw",          label: "Raw Folder",   color: "#f59e0b", bg: "#fffbeb", border: "#fcd34d" },
  { key: "selected_raw", label: "Selected Raw",  color: "#3b82f6", bg: "#eff6ff", border: "#93c5fd" },
  { key: "soft_copy",    label: "Soft Copy",     color: "#10b981", bg: "#ecfdf5", border: "#6ee7b7" },
];

function makePhoto(file, folderType) {
  photoIdCounter++;
  return {
    id:         photoIdCounter,
    photoId:    null,
    filePath:   null,
    file:       file,
    name:       file.name,
    preview:    URL.createObjectURL(file),
    color:      COLORS[photoIdCounter % COLORS.length],
    isExisting: false,
    folderType: folderType || "raw",
  };
}

// ── SVG Icon Library ──────────────────────────────────────────────────────────
function Icon({ name, size = 18, color = "currentColor", strokeWidth = 2 }) {
  const s = { width: size, height: size, display: "block", flexShrink: 0 };
  const p = { fill: "none", stroke: color, strokeWidth, strokeLinecap: "round", strokeLinejoin: "round" };

  const icons = {
    // Toast icons
    check_circle: (
      <svg viewBox="0 0 24 24" style={s} {...p}>
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
        <polyline points="22 4 12 14.01 9 11.01"/>
      </svg>
    ),
    x_circle: (
      <svg viewBox="0 0 24 24" style={s} {...p}>
        <circle cx="12" cy="12" r="10"/>
        <line x1="15" y1="9" x2="9" y2="15"/>
        <line x1="9" y1="9" x2="15" y2="15"/>
      </svg>
    ),
    alert_triangle: (
      <svg viewBox="0 0 24 24" style={s} {...p}>
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
        <line x1="12" y1="9" x2="12" y2="13"/>
        <line x1="12" y1="17" x2="12.01" y2="17"/>
      </svg>
    ),
    info: (
      <svg viewBox="0 0 24 24" style={s} {...p}>
        <circle cx="12" cy="12" r="10"/>
        <line x1="12" y1="16" x2="12" y2="12"/>
        <line x1="12" y1="8" x2="12.01" y2="8"/>
      </svg>
    ),
    // Step indicator
    user: (
      <svg viewBox="0 0 24 24" style={s} {...p}>
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
        <circle cx="12" cy="7" r="4"/>
      </svg>
    ),
    hospital: (
      <svg viewBox="0 0 24 24" style={s} {...p}>
        <rect x="3" y="3" width="18" height="18" rx="2"/>
        <path d="M9 12h6"/><path d="M12 9v6"/>
      </svg>
    ),
    baby: (
      <svg viewBox="0 0 24 24" style={s} {...p}>
        <circle cx="12" cy="7" r="3"/>
        <path d="M6 21v-2a6 6 0 0 1 12 0v2"/>
        <path d="M9 10.5c0 .83-.67 1.5-1.5 1.5S6 11.33 6 10.5"/>
        <path d="M15 10.5c0 .83.67 1.5 1.5 1.5S18 11.33 18 10.5"/>
      </svg>
    ),
    camera: (
      <svg viewBox="0 0 24 24" style={s} {...p}>
        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
        <circle cx="12" cy="13" r="4"/>
      </svg>
    ),
    check: (
      <svg viewBox="0 0 24 24" style={s} {...p}>
        <polyline points="20 6 9 17 4 12"/>
      </svg>
    ),
    // Notices
    folder: (
      <svg viewBox="0 0 24 24" style={s} {...p}>
        <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
      </svg>
    ),
    // Empty states
    phone: (
      <svg viewBox="0 0 24 24" style={s} {...p}>
        <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
        <line x1="12" y1="18" x2="12.01" y2="18"/>
      </svg>
    ),
    building: (
      <svg viewBox="0 0 24 24" style={s} {...p}>
        <rect x="2" y="3" width="20" height="18" rx="2"/>
        <path d="M8 3v18M16 3v18M2 9h20M2 15h20"/>
      </svg>
    ),
    users: (
      <svg viewBox="0 0 24 24" style={s} {...p}>
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
        <circle cx="9" cy="7" r="4"/>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
      </svg>
    ),
    // Photo grid
    search: (
      <svg viewBox="0 0 24 24" style={s} {...p}>
        <circle cx="11" cy="11" r="8"/>
        <line x1="21" y1="21" x2="16.65" y2="16.65"/>
      </svg>
    ),
    plus: (
      <svg viewBox="0 0 24 24" style={s} {...p}>
        <line x1="12" y1="5" x2="12" y2="19"/>
        <line x1="5" y1="12" x2="19" y2="12"/>
      </svg>
    ),
    image: (
      <svg viewBox="0 0 24 24" style={s} {...p}>
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
        <circle cx="8.5" cy="8.5" r="1.5"/>
        <polyline points="21 15 16 10 5 21"/>
      </svg>
    ),
    // Lightbox / upload buttons
    download: (
      <svg viewBox="0 0 24 24" style={s} {...p}>
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
        <polyline points="7 10 12 15 17 10"/>
        <line x1="12" y1="15" x2="12" y2="3"/>
      </svg>
    ),
    upload: (
      <svg viewBox="0 0 24 24" style={s} {...p}>
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
        <polyline points="17 8 12 3 7 8"/>
        <line x1="12" y1="3" x2="12" y2="15"/>
      </svg>
    ),
    x: (
      <svg viewBox="0 0 24 24" style={s} {...p}>
        <line x1="18" y1="6" x2="6" y2="18"/>
        <line x1="6" y1="6" x2="18" y2="18"/>
      </svg>
    ),
    chevron_left: (
      <svg viewBox="0 0 24 24" style={s} {...p}>
        <polyline points="15 18 9 12 15 6"/>
      </svg>
    ),
    chevron_right: (
      <svg viewBox="0 0 24 24" style={s} {...p}>
        <polyline points="9 18 15 12 9 6"/>
      </svg>
    ),
    chevron_up: (
      <svg viewBox="0 0 24 24" style={s} {...p}>
        <polyline points="18 15 12 9 6 15"/>
      </svg>
    ),
    chevron_down: (
      <svg viewBox="0 0 24 24" style={s} {...p}>
        <polyline points="6 9 12 15 18 9"/>
      </svg>
    ),
    // Upload button states
    clock: (
      <svg viewBox="0 0 24 24" style={s} {...p}>
        <circle cx="12" cy="12" r="10"/>
        <polyline points="12 6 12 12 16 14"/>
      </svg>
    ),
    rocket: (
      <svg viewBox="0 0 24 24" style={s} {...p}>
        <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/>
        <path d="m3.29 15.18 1.32-4.48 4.18 4.18-4.48 1.32z"/>
        <path d="M20.75 3.25a17.63 17.63 0 0 0-13.07 5.43l-.39.44 4.65 4.65.44-.39a17.63 17.63 0 0 0 5.43-13.07l-.06-1.06z"/>
        <path d="M15.5 8.5a1 1 0 1 0 0-2 1 1 0 0 0 0 2z" fill={color} stroke="none"/>
      </svg>
    ),
    trash: (
      <svg viewBox="0 0 24 24" style={s} {...p}>
        <polyline points="3 6 5 6 21 6"/>
        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
      </svg>
    ),
  };

  return icons[name] || null;
}

// ── Toast ─────────────────────────────────────────────────────────────────────
const TOAST_STYLES = {
  success: { bg:"#ecfdf5", border:"#6ee7b7", progress:"#10b981", iconName:"check_circle", iconColor:"#10b981", titleColor:"#065f46", subColor:"#047857" },
  error:   { bg:"#fef2f2", border:"#fca5a5", progress:"#ef4444", iconName:"x_circle",     iconColor:"#ef4444", titleColor:"#991b1b", subColor:"#b91c1c" },
  warning: { bg:"#fffbeb", border:"#fcd34d", progress:"#f59e0b", iconName:"alert_triangle",iconColor:"#f59e0b", titleColor:"#78350f", subColor:"#92400e" },
  info:    { bg:"#eff6ff", border:"#93c5fd", progress:"#3b82f6", iconName:"info",          iconColor:"#3b82f6", titleColor:"#1e3a5f", subColor:"#1d4ed8" },
};

function Toast({ toasts, onDismiss }) {
  return (
    <>
      <style>{`
        @keyframes toastSlideIn { from{transform:translateX(110%);opacity:0} to{transform:translateX(0);opacity:1} }
        @keyframes toastProgress { from{width:100%} to{width:0%} }
        .toast-item { animation: toastSlideIn 0.3s cubic-bezier(0.34,1.56,0.64,1); }
        .toast-close-btn:hover { opacity:1 !important; }
      `}</style>
      <div role="region" aria-live="polite" style={{
        position:"fixed", bottom:24, right:24, display:"flex", flexDirection:"column",
        gap:10, zIndex:99999, alignItems:"flex-end", pointerEvents:"none",
      }}>
        {toasts.map(t => {
          const s = TOAST_STYLES[t.type] || TOAST_STYLES.info;
          return (
            <div key={t.id} className="toast-item" role="alert" style={{
              display:"flex", alignItems:"flex-start", gap:10, padding:"12px 16px",
              borderRadius:12, minWidth:280, maxWidth:360, fontSize:14,
              background:s.bg, border:`1px solid ${s.border}`,
              boxShadow:"0 4px 20px rgba(0,0,0,0.13)",
              position:"relative", overflow:"hidden", pointerEvents:"all",
            }}>
              <span style={{ marginTop:1 }}>
                <Icon name={s.iconName} size={18} color={s.iconColor} />
              </span>
              <div style={{ flex:1, minWidth:0 }}>
                <p style={{ fontWeight:600, margin:"0 0 2px", color:s.titleColor, fontSize:14, lineHeight:1.3 }}>{t.title}</p>
                {t.sub && <p style={{ fontSize:12, margin:0, color:s.subColor, lineHeight:1.4, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{t.sub}</p>}
              </div>
              <button className="toast-close-btn" onClick={() => onDismiss(t.id)}
                style={{ background:"none", border:"none", cursor:"pointer", opacity:0.45, padding:"0 0 0 4px", lineHeight:1, color:s.titleColor, flexShrink:0, marginTop:1, transition:"opacity 0.15s" }}>
                <Icon name="x" size={15} color={s.titleColor} />
              </button>
              <div style={{ position:"absolute", bottom:0, left:0, height:3, background:s.progress,
                borderRadius:"0 0 12px 12px", animation:`toastProgress ${t.duration}ms linear forwards` }} />
            </div>
          );
        })}
      </div>
    </>
  );
}

function useToast() {
  const [toasts, setToasts] = useState([]);
  const addToast = useCallback(({ type = "info", title, sub, duration = 4500 }) => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, type, title, sub, duration }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), duration + 100);
  }, []);
  const dismiss = useCallback((id) => setToasts(prev => prev.filter(t => t.id !== id)), []);
  return { toasts, addToast, dismiss };
}

// ── Lightbox ──────────────────────────────────────────────────────────────────
function Lightbox({ photo, photos, onClose, onPrev, onNext, viewUrl }) {
  const fullUrl      = photo.preview;
  const currentIndex = photos.findIndex(p => p.id === photo.id);

  useEffect(() => {
    const h = (e) => {
      if (e.key === "Escape")     onClose();
      if (e.key === "ArrowRight") onNext();
      if (e.key === "ArrowLeft")  onPrev();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose, onNext, onPrev]);

  const handleDownload = async () => {
    try {
      const blob = await (await fetch(fullUrl)).blob();
      const url  = URL.createObjectURL(blob);
      Object.assign(document.createElement("a"), { href:url, download:photo.name }).click();
      URL.revokeObjectURL(url);
    } catch { window.open(fullUrl, "_blank"); }
  };

  const overlayBtn = {
    background:"rgba(255,255,255,0.15)", border:"1px solid rgba(255,255,255,0.3)",
    borderRadius:8, color:"#fff", cursor:"pointer", display:"flex", alignItems:"center", gap:6,
  };

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.92)", zIndex:9999,
      display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center" }}
      onClick={onClose}>

      {/* Header */}
      <div style={{ position:"absolute", top:0, left:0, right:0, display:"flex",
        alignItems:"center", justifyContent:"space-between", padding:"14px 20px",
        background:"rgba(0,0,0,0.5)", zIndex:10 }} onClick={e => e.stopPropagation()}>
        <span style={{ color:"#fff", fontSize:14, opacity:0.8 }}>{photo.name}</span>
        <span style={{ color:"#fff", fontSize:13, opacity:0.6 }}>{currentIndex + 1} / {photos.length}</span>
        <div style={{ display:"flex", gap:10 }}>
          <button onClick={handleDownload} style={{ ...overlayBtn, padding:"6px 14px", fontSize:13 }}>
            <Icon name="download" size={15} color="#fff" /> Download
          </button>
          <button onClick={onClose} style={{ ...overlayBtn, padding:"6px 10px" }}>
            <Icon name="x" size={16} color="#fff" />
          </button>
        </div>
      </div>

      {/* Prev */}
      {photos.length > 1 && (
        <button onClick={e => { e.stopPropagation(); onPrev(); }} style={{
          ...overlayBtn, position:"absolute", left:16, top:"50%", transform:"translateY(-50%)",
          borderRadius:"50%", width:44, height:44, justifyContent:"center", zIndex:10 }}>
          <Icon name="chevron_left" size={22} color="#fff" />
        </button>
      )}

      <img src={fullUrl} alt={photo.name} onClick={e => e.stopPropagation()}
        style={{ maxWidth:"90vw", maxHeight:"82vh", objectFit:"contain", borderRadius:8, userSelect:"none" }} />

      {/* Next */}
      {photos.length > 1 && (
        <button onClick={e => { e.stopPropagation(); onNext(); }} style={{
          ...overlayBtn, position:"absolute", right:16, top:"50%", transform:"translateY(-50%)",
          borderRadius:"50%", width:44, height:44, justifyContent:"center", zIndex:10 }}>
          <Icon name="chevron_right" size={22} color="#fff" />
        </button>
      )}

      {/* Thumbnail strip */}
      {photos.length > 1 && (
        <div style={{ position:"absolute", bottom:0, left:0, right:0, display:"flex", gap:6,
          padding:"12px 16px", overflowX:"auto", background:"rgba(0,0,0,0.5)" }}
          onClick={e => e.stopPropagation()}>
          {photos.map((p, idx) => (
            <img key={p.id} src={p.preview} alt={p.name}
              onClick={() => { const d = idx - currentIndex; if(d>0) for(let i=0;i<d;i++) onNext(); else if(d<0) for(let i=0;i<Math.abs(d);i++) onPrev(); }}
              style={{ width:48, height:48, objectFit:"cover", borderRadius:6, flexShrink:0, cursor:"pointer",
                border: p.id === photo.id ? "2px solid #fff" : "2px solid transparent",
                opacity: p.id === photo.id ? 1 : 0.55, transition:"opacity 0.15s" }} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Step Indicator ────────────────────────────────────────────────────────────
const STEPS = [
  { iconName:"user",     label:"PHOTOGRAPHER" },
  { iconName:"hospital", label:"HOSPITAL"     },
  { iconName:"users",    label:"CUSTOMER"     },
  { iconName:"camera",   label:"PHOTOS"       },
];

function StepIndicator({ currentStep }) {
  return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:0, marginBottom:32 }}>
      {STEPS.map((step, i) => {
        const isDone   = i < currentStep;
        const isActive = i === currentStep;
        const color     = isDone ? "#10b981" : isActive ? "#3b82f6" : "#d1d5db";
        const textColor = isDone ? "#065f46" : isActive ? "#1e40af" : "#9ca3af";
        return (
          <div key={i} style={{ display:"flex", alignItems:"center" }}>
            <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:4 }}>
              <div style={{
                width:38, height:38, borderRadius:"50%",
                background: isDone ? "#d1fae5" : isActive ? "#dbeafe" : "#f3f4f6",
                border:`2px solid ${color}`,
                display:"flex", alignItems:"center", justifyContent:"center",
                transition:"all 0.3s",
              }}>
                {isDone
                  ? <Icon name="check" size={17} color="#10b981" strokeWidth={2.5} />
                  : <Icon name={step.iconName} size={16} color={color} strokeWidth={1.8} />
                }
              </div>
              <span style={{ fontSize:10, fontWeight:600, color:textColor, whiteSpace:"nowrap", letterSpacing:"0.04em" }}>
                {step.label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div style={{
                width:48, height:2, margin:"0 4px", marginBottom:18,
                background: i < currentStep ? "#10b981" : "#e5e7eb",
                transition:"background 0.3s",
              }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Searchable Select ─────────────────────────────────────────────────────────
function SearchableSelect({ label, placeholder, options, value, onChange, loading, disabled, getLabel, getId }) {
  const [query, setQuery] = useState("");
  const [open,  setOpen]  = useState(false);
  const wrapRef           = useRef();

  const filtered = options.filter(o => getLabel(o).toLowerCase().includes(query.toLowerCase()));
  const selected  = options.find(o => getId(o) === value);

  useEffect(() => {
    const h = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  useEffect(() => { setQuery(""); setOpen(false); }, [options]);

  const handleSelect = (opt) => { onChange(opt); setQuery(""); setOpen(false); };

  return (
    <div className="field-group">
      <label className="field-label">{label}</label>
      <div ref={wrapRef} style={{ position:"relative" }}>
        <div
          onClick={() => { if (!disabled && !loading) setOpen(v => !v); }}
          style={{
            display:"flex", alignItems:"center", justifyContent:"space-between",
            padding:"10px 14px", borderRadius:10, fontSize:14,
            background: disabled ? "#f9fafb" : "#fff",
            border:`1.5px solid ${open ? "#3b82f6" : "#e2e8f0"}`,
            cursor: disabled ? "not-allowed" : "pointer",
            transition:"border-color 0.2s", minHeight:44,
            color: selected ? "#111827" : "#9ca3af",
          }}
        >
          <span style={{ flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
            {loading ? "Loading…" : selected ? getLabel(selected) : placeholder}
          </span>
          {loading
            ? <span className="input-spinner" />
            : <Icon name={open ? "chevron_up" : "chevron_down"} size={15} color="#9ca3af" />
          }
        </div>

        {open && (
          <div style={{
            position:"absolute", top:"calc(100% + 6px)", left:0, right:0,
            background:"#fff", borderRadius:10, border:"1.5px solid #e2e8f0",
            boxShadow:"0 8px 32px rgba(0,0,0,0.12)", zIndex:1000,
            maxHeight:240, display:"flex", flexDirection:"column", overflow:"hidden",
          }}>
            <div style={{ padding:"8px 10px", borderBottom:"1px solid #f3f4f6" }}>
              <input autoFocus value={query} onChange={e => setQuery(e.target.value)} placeholder="Search…"
                style={{ width:"100%", border:"1.5px solid #e2e8f0", borderRadius:7,
                  padding:"6px 10px", fontSize:13, outline:"none", background:"#f9fafb" }} />
            </div>
            <div style={{ overflowY:"auto", flex:1 }}>
              {filtered.length === 0
                ? <div style={{ padding:"16px 14px", color:"#9ca3af", fontSize:13, textAlign:"center" }}>No results</div>
                : filtered.map(opt => (
                  <div key={getId(opt)} onClick={() => handleSelect(opt)}
                    style={{
                      padding:"10px 14px", fontSize:14, cursor:"pointer",
                      background: getId(opt) === value ? "#eff6ff" : "transparent",
                      color:      getId(opt) === value ? "#1d4ed8" : "#111827",
                      fontWeight: getId(opt) === value ? 600 : 400,
                      transition:"background 0.12s",
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = getId(opt) === value ? "#eff6ff" : "#f9fafb"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = getId(opt) === value ? "#eff6ff" : "transparent"; }}
                  >
                    {getLabel(opt)}
                  </div>
                ))
              }
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function PhotoUploadPage() {
  const urls                           = useSelector((state) => state.globalurl);
  const { toasts, addToast, dismiss } = useToast();

  const [photographers,        setPhotographers]        = useState([]);
  const [loadingPhotographers, setLoadingPhotographers] = useState(false);
  const [selectedPhotographer, setSelectedPhotographer] = useState(null);

  const [hospitals,        setHospitals]        = useState([]);
  const [loadingHospitals, setLoadingHospitals] = useState(false);
  const [selectedHospital, setSelectedHospital] = useState(null);

  const [customers,        setCustomers]        = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const [photos,         setPhotos]         = useState([]);
  const [uploading,      setUploading]      = useState(false);
  const [submitted,      setSubmitted]      = useState(false);
  const [dragging,       setDragging]       = useState(false);
  const [refreshKey,     setRefreshKey]     = useState(0);
  const [selectedFolder, setSelectedFolder] = useState("raw");
  const [draggingFolder, setDraggingFolder] = useState(null);

  const [lightbox,        setLightbox]        = useState(null);
  const [lightboxIndex,   setLightboxIndex]   = useState(0);
  const [uploadProgress,  setUploadProgress]  = useState(0);
  const [uploadPhase,     setUploadPhase]     = useState("");
  const [uploadCount,     setUploadCount]     = useState(0);

  const fileRef = useRef();

  // ── Derived ───────────────────────────────────────────────────────────────
  const order = selectedCustomer ? {
    OrderID:        selectedCustomer.OrderID,
    OrderNo:        selectedCustomer.OrderNo,
    OrderStatus:    selectedCustomer.OrderStatus,
    BillingDate:    selectedCustomer.BillingDate,
    RequiredPhotos: selectedCustomer.RequiredPhotos,
    NoOfCopies:     selectedCustomer.NoOfCopies,
    FrameSize:      selectedCustomer.FrameSize,
  } : null;

  const packageInfo = selectedCustomer ? {
    PackageID:    selectedCustomer.PackageID,
    PackageName:  selectedCustomer.PackageName,
    PackagePrice: selectedCustomer.PackagePrice,
  } : null;

  const required             = parseInt(order?.RequiredPhotos) || 0;
  const newPhotos            = photos.filter(p => !p.isExisting);
  const existingCount        = photos.filter(p => p.isExisting).length;
  const selectedFolderPhotos = newPhotos.filter(p => p.folderType === selectedFolder);
  const activeFolderLabel    = FOLDERS.find(f => f.key === selectedFolder)?.label || "";
  const isReady              = selectedCustomer && order?.OrderID && selectedFolderPhotos.length > 0;
  const uploadPercent        = required > 0 ? Math.min(100, Math.round((photos.length / required) * 100)) : 0;
  const totalSizeMB          = (selectedFolderPhotos.length * 8.9).toFixed(0);

  const currentStep = !selectedPhotographer ? 0
    : !selectedHospital  ? 1
    : !selectedCustomer  ? 2
    : 3;

  // ── LOAD: Photographers ───────────────────────────────────────────────────
  useEffect(() => {
    const fetch_ = async () => {
      setLoadingPhotographers(true);
      try {
        const url    = store.getState().globalurl.PeopleGetUrl;
        const params = { data: JSON.stringify({ Query: { Action: "getall", PeopleType: "employee" } }) };
        const result = await getApi(url, params, "");
        if (result?.Status === "Y") {
          setPhotographers((result.Data || []).map(p => {
            const fullName = p.PeopleName ?? p.EmployeeName ?? p.Name ?? p.name ?? p.label ??
              ([p.FirstName, p.LastName].filter(Boolean).join(" ") || "Employee");
            return { ...p, recordId: p.RecordID ?? p.PeopleID ?? p.EmployeeID ?? p.id, label: fullName.trim() };
          }));
        } else {
          addToast({ type:"error", title:"Failed", sub: result?.Message || "Could not load photographers." });
        }
      } catch (err) {
        console.error("Photographers fetch error:", err);
        addToast({ type:"error", title:"Error", sub:"Could not load photographers." });
      } finally { setLoadingPhotographers(false); }
    };
    fetch_();
  }, []);

  // ── LOAD: Hospitals ───────────────────────────────────────────────────────
  useEffect(() => {
    const fetch_ = async () => {
      setLoadingHospitals(true);
      try {
        const url    = store.getState().globalurl.HospitalsGetUrl;
        const params = { data: JSON.stringify({ Query: { Action: "getall", RecordID: "" } }) };
        const result = await getApi(url, params, "");
        if (result?.Status === "Y") {
          setHospitals((result.Data || []).map(h => ({
            ...h,
            recordId:     h.RecordID     ?? h.HospitalID ?? h.id,
            label:        h.HospitalName ?? h.name       ?? h.label ?? "Hospital",
            locationName: h.LocationName ?? h.Location   ?? h.City  ?? null,
          })));
        } else {
          addToast({ type:"error", title:"Failed", sub: result?.Message || "Could not load hospitals." });
        }
      } catch (err) {
        console.error("Hospitals fetch error:", err);
        addToast({ type:"error", title:"Error", sub:"Could not load hospitals." });
      } finally { setLoadingHospitals(false); }
    };
    fetch_();
  }, []);

  // ── LOAD: Customers (photographer + hospital) ─────────────────────────────
  useEffect(() => {
    setSelectedCustomer(null);
    setCustomers([]);
    setPhotos([]);
    setSubmitted(false);
    if (!selectedPhotographer || !selectedHospital) return;

    const fetch_ = async () => {
      setLoadingCustomers(true);
      try {
        const params = {
          data: JSON.stringify({
            Query: {
              Action:         "get_customers",
              PhotographerID: selectedPhotographer.recordId,
              HospitalID:     selectedHospital.recordId,
            }
          })
        };
        const result = await getApi(urls.OrderPhotoGetUrl, params);
        if (result?.Status === "Y") {
          setCustomers(result.Data?.Customers || []);
        } else {
          addToast({ type:"error", title:"Failed to load customers", sub: result?.Data || "" });
        }
      } catch (err) {
        console.error("Customers fetch error:", err);
        addToast({ type:"error", title:"Error", sub:"Could not load customers." });
      } finally { setLoadingCustomers(false); }
    };
    fetch_();
  }, [selectedPhotographer, selectedHospital]);

  // ── LOAD: Existing photos ─────────────────────────────────────────────────
  useEffect(() => {
    setPhotos([]);
    setSubmitted(false);
    if (!selectedCustomer?.OrderID) return;

    const fetch_ = async () => {
      try {
        const params = {
          data: JSON.stringify({ Query: { Action: "get_photos", OrderID: selectedCustomer.OrderID } })
        };
        const result = await getApi(urls.OrderPhotoGetUrl, params);
        if (result?.Status === "Y") {
          const list = result.Data?.Photos?.List || [];
          setPhotos(list.map(p => ({
            id:           parseInt(p.PhotoID),
            photoId:      parseInt(p.PhotoID),
            filePath:     p.FilePath,
            googleFileId: p.GoogleFileId,
            file:         null,
            name:         p.FileName,
            preview:      urls.OrderPhotoProxyUrl + '?id=' + p.GoogleFileId,
            color:        COLORS[parseInt(p.PhotoID) % COLORS.length],
            isExisting:   true,
            folderType:   p.FolderType || p.folderType || p.Folder || "raw",
          })));
        }
      } catch (err) { console.error("Photos fetch error:", err); }
    };
    fetch_();
  }, [selectedCustomer, refreshKey]);

  // ── Lightbox ──────────────────────────────────────────────────────────────
  const openLightbox  = (photo) => { setLightboxIndex(photos.findIndex(p => p.id === photo.id)); setLightbox(photo); };
  const closeLightbox = useCallback(() => setLightbox(null), []);
  const goPrev        = useCallback(() => setLightboxIndex(prev => { const i = (prev - 1 + photos.length) % photos.length; setLightbox(photos[i]); return i; }), [photos]);
  const goNext        = useCallback(() => setLightboxIndex(prev => { const i = (prev + 1) % photos.length; setLightbox(photos[i]); return i; }), [photos]);

  // ── Files ─────────────────────────────────────────────────────────────────
  const addFiles = (files, folderType) => {
    if (!selectedCustomer) return;
    const folder      = folderType || selectedFolder;
    const folderLabel = FOLDERS.find(f => f.key === folder)?.label || folder;
    const added       = Array.from(files).map(f => makePhoto(f, folder));
    setPhotos(prev => [...prev, ...added]);
    setSubmitted(false);
    addToast({ type:"info", title:`${added.length} photo${added.length !== 1 ? "s" : ""} added to ${folderLabel}`, sub:"Click Upload when ready." });
  };

  const removePhoto = async (e, id) => {
    e.stopPropagation();
    const photo = photos.find(p => p.id === id);
    if (!photo) return;
    if (photo.isExisting) {
      try {
        await postApi(urls.OrderPhotoPostUrl, {
          action: "delete",
          data: { OP_RECORDID: photo.photoId, OP_GOOGLEFILEID: photo.googleFileId }
        });
        addToast({ type:"success", title:"Photo deleted" });
      } catch (err) {
        console.error("Delete error →", err);
        addToast({ type:"error", title:"Delete failed", sub:"Please try again." });
        return;
      }
    }
    setPhotos(prev => prev.filter(p => p.id !== id));
    setSubmitted(false);
  };

  const handleDrop     = (e) => { e.preventDefault(); setDragging(false); if (e.dataTransfer.files.length) addFiles(e.dataTransfer.files); };
  const handleClearNew = () => {
    const count = newPhotos.length;
    setPhotos(prev => prev.filter(p => p.isExisting));
    setSubmitted(false);
    addToast({ type:"info", title:`${count} new photo${count !== 1 ? "s" : ""} cleared`, sub:"Previously uploaded photos are still saved." });
  };

  // ── Upload (only the currently selected folder) ───────────────────────────
  const handleUpload = async () => {
    if (!isReady || uploading) return;
    const filesToUpload = selectedFolderPhotos.filter(p => p.file);
    if (filesToUpload.length === 0) { setSubmitted(true); return; }

    setUploading(true);
    setUploadProgress(0);
    setUploadPhase("sending");
    setUploadCount(filesToUpload.length);

    try {
      const formData = new FormData();
      formData.append("action",           "insert");
      formData.append("OP_ORDERID",       order.OrderID);
      formData.append("OP_ORDERNO",       order.OrderNo);
      formData.append("OP_PEOPLEID",      selectedCustomer.PeopleID);
      formData.append("OP_UPLOADEDBY",    selectedPhotographer.recordId);
      formData.append("OP_FOLDERTYPE",    selectedFolder);
      formData.append("OP_HOSPITALNAME",  selectedHospital.label);
      formData.append("OP_CUSTOMERNAME",  selectedCustomer.CustomerName);
      filesToUpload.forEach(p => formData.append("photos[]", p.file));

      const res = await postMultipartApi(urls.OrderPhotoPostUrl, formData, null, (pct) => {
        setUploadProgress(pct);
        if (pct === 100) setUploadPhase("saving");
      });

      if (res.Status === "Y") {
        if (res.FailedCount > 0) {
          addToast({ type:"warning", title:`${res.FailedCount} photo${res.FailedCount !== 1 ? "s" : ""} failed`, sub:`${filesToUpload.length - res.FailedCount} saved · ${activeFolderLabel}` });
        } else {
          addToast({ type:"success", title:`${filesToUpload.length} photo${filesToUpload.length !== 1 ? "s" : ""} uploaded!`, sub:`${activeFolderLabel} · Order ${order.OrderNo}` });
        }
        setSubmitted(true);
        setRefreshKey(k => k + 1);
      } else {
        addToast({ type:"error", title:`Upload failed — ${activeFolderLabel}`, sub: res.Data || "Something went wrong." });
      }
    } catch (err) {
      console.error("Upload error →", err);
      addToast({ type:"error", title:"Upload failed", sub:"Network error. Check your connection." });
    } finally { setUploading(false); setUploadPhase(""); setUploadProgress(0); }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="app">
      <Toast toasts={toasts} onDismiss={dismiss} />

      {lightbox && (
        <Lightbox photo={lightbox} photos={photos} onClose={closeLightbox}
          onPrev={goPrev} onNext={goNext} viewUrl={urls.OrderPhotoViewUrl} />
      )}

      <nav className="navbar">
        <div className="nav-left">
          <img src="/logo.png" alt="Hands In Heart" className="nav-logo" />
          <span className="portal-badge">PHOTOGRAPHER PORTAL</span>
        </div>
      </nav>

      <main className="main">
        <div className="page-header">
          <h1 className="page-title">
            <span style={{ display:"inline-flex", verticalAlign:"middle", marginRight:8 }}>
              <Icon name="camera" size={28} color="#3b82f6" strokeWidth={1.8} />
            </span>
            Upload Shoot Photos
          </h1>
          <p className="page-subtitle">
            Select the photographer and hospital, then choose the customer to load their order.
          </p>
        </div>

        <StepIndicator currentStep={currentStep} />

        {/* ── Selectors Row ── */}
        <div className="fields-row">
          <SearchableSelect
            label="PHOTOGRAPHER"
            placeholder="Select photographer…"
            options={photographers}
            value={selectedPhotographer?.recordId}
            onChange={(opt) => {
              setSelectedPhotographer(opt);
              setSelectedHospital(null);
              setSelectedCustomer(null);
              setCustomers([]);
              setPhotos([]);
            }}
            loading={loadingPhotographers}
            disabled={false}
            getLabel={(o) => o.label}
            getId={(o) => o.recordId}
          />
          <SearchableSelect
            label="HOSPITAL / BRANCH"
            placeholder={!selectedPhotographer ? "Select photographer first" : "Select hospital…"}
            options={hospitals}
            value={selectedHospital?.recordId}
            onChange={(opt) => setSelectedHospital(opt)}
            loading={loadingHospitals}
            disabled={!selectedPhotographer}
            getLabel={(o) => o.locationName ? `${o.label} — ${o.locationName}` : o.label}
            getId={(o) => o.recordId}
          />
          <SearchableSelect
            label="CUSTOMER"
            placeholder={
              !selectedPhotographer || !selectedHospital
                ? "Select photographer & hospital first"
                : customers.length === 0 ? "No customers found" : "Select customer…"
            }
            options={customers}
            value={selectedCustomer?.PeopleID}
            onChange={(opt) => setSelectedCustomer(opt)}
            loading={loadingCustomers}
            disabled={!selectedPhotographer || !selectedHospital}
            getLabel={(o) => `${o.CustomerName}${o.Mobile ? " · " + o.Mobile : ""}${o.OrderNo ? " · " + o.OrderNo : ""}`}
            getId={(o) => o.PeopleID}
          />
        </div>

        {/* Notices */}
        {selectedCustomer && !order?.OrderID && (
          <div className="notice notice--warn">
            <span className="notice-icon">
              <Icon name="alert_triangle" size={18} color="#f59e0b" />
            </span>
            <span>Customer <strong>{selectedCustomer.CustomerName}</strong> has no order yet.</span>
          </div>
        )}

        {selectedCustomer && order?.OrderID && existingCount > 0 && (
          <div className="notice notice--info">
            <span className="notice-icon">
              <Icon name="folder" size={18} color="#3b82f6" />
            </span>
            <span>
              <strong>{existingCount}</strong> photo{existingCount !== 1 ? "s" : ""} already uploaded — shown folder-wise below. Click any photo to view full size.
            </span>
          </div>
        )}

        {/* ── Folder Picker + Drop Zone ── */}
        {selectedCustomer && order?.OrderID && (
          <>
            {/* Hidden file input */}
            <input ref={fileRef} type="file" multiple accept=".jpg,.jpeg,.png,.raw,.heic"
              style={{ display:"none" }}
              onChange={e => { addFiles(e.target.files, selectedFolder); e.target.value = ""; }} />

            {/* Three folder cards */}
            <div className="folder-picker">
              {FOLDERS.map(f => {
                const uploaded   = photos.filter(p =>  p.isExisting && p.folderType === f.key).length;
                const newCount   = photos.filter(p => !p.isExisting && p.folderType === f.key).length;
                const total      = uploaded + newCount;
                const isSelected = selectedFolder === f.key;
                const isDragOver = draggingFolder === f.key;
                return (
                  <div
                    key={f.key}
                    className={`folder-card${isSelected ? " folder-card--selected" : ""}${isDragOver ? " folder-card--dragover" : ""}`}
                    style={{ "--fc": f.color, "--fb": f.bg, "--fbd": f.border }}
                    onClick={() => { setSelectedFolder(f.key); setSubmitted(false); }}
                    onDrop={e => { e.preventDefault(); setDraggingFolder(null); addFiles(e.dataTransfer.files, f.key); }}
                    onDragOver={e => { e.preventDefault(); setDraggingFolder(f.key); }}
                    onDragLeave={() => setDraggingFolder(null)}
                  >
                    <div className="folder-card-icon">
                      <Icon name="folder" size={30} color={isSelected || isDragOver ? f.color : "#94a3b8"} strokeWidth={isSelected ? 2 : 1.8} />
                    </div>
                    <div className="folder-card-name" style={{ color: isSelected || isDragOver ? f.color : "#374151" }}>{f.label}</div>
                    <div className="folder-card-count">
                      {total === 0
                        ? "Empty"
                        : <>
                            {uploaded > 0 && <span>{uploaded} uploaded</span>}
                            {uploaded > 0 && newCount > 0 && <span style={{ opacity:0.5 }}> · </span>}
                            {newCount > 0 && <span style={{ color: f.color, fontWeight:700 }}>{newCount} new</span>}
                          </>
                      }
                    </div>
                    {isSelected && (
                      <span className="folder-card-active-dot" style={{ background: f.color }} />
                    )}
                  </div>
                );
              })}
            </div>

            {/* Drop zone — always uses the selected folder */}
            <div
              className={`dropzone ${dragging ? "dropzone--active" : ""}`}
              onDrop={handleDrop}
              onDragOver={e => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onClick={() => fileRef.current.click()}
            >
              <div className="dropzone-icon">
                <Icon name="upload" size={28} color="white" strokeWidth={2} />
              </div>
              <p className="dropzone-title">
                {dragging
                  ? `Drop to add to ${FOLDERS.find(f => f.key === selectedFolder)?.label}`
                  : "Click to browse or drag photos here"}
              </p>
              <p className="dropzone-sub">
                Adding to: <strong>{FOLDERS.find(f => f.key === selectedFolder)?.label}</strong>
                {" "}· JPG · JPEG · PNG · RAW · HEIC
              </p>
            </div>

            {required > 0 && (
              <div className="progress-row">
                <div className="progress-track">
                  <div className="progress-fill" style={{ width:`${uploadPercent}%` }} />
                </div>
                <span className="progress-label">{photos.length} / {required} photos</span>
              </div>
            )}
          </>
        )}

        {/* ── Empty / Guidance States ── */}
        {!selectedPhotographer && !loadingPhotographers && (
          <div className="empty-state">
            <div className="empty-icon" style={{ display:"flex", alignItems:"center", justifyContent:"center" }}>
              <Icon name="user" size={40} color="#93c5fd" strokeWidth={1.5} />
            </div>
            <p className="empty-title">Select a photographer to begin</p>
            <p className="empty-sub">
              Choose the photographer, then the hospital. The customer list will be filtered
              to only show customers whose orders were created by that photographer at that hospital.
            </p>
          </div>
        )}

        {selectedPhotographer && !selectedHospital && !loadingHospitals && (
          <div className="empty-state">
            <div className="empty-icon" style={{ display:"flex", alignItems:"center", justifyContent:"center" }}>
              <Icon name="building" size={40} color="#93c5fd" strokeWidth={1.5} />
            </div>
            <p className="empty-title">Now select a hospital</p>
            <p className="empty-sub">
              Customers will be fetched for <strong>{selectedPhotographer.label}</strong> at the hospital you choose.
            </p>
          </div>
        )}

        {selectedPhotographer && selectedHospital && !selectedCustomer && !loadingCustomers && (
          <div className="empty-state">
            <div className="empty-icon" style={{ display:"flex", alignItems:"center", justifyContent:"center" }}>
              <Icon name="users" size={40} color="#93c5fd" strokeWidth={1.5} />
            </div>
            <p className="empty-title">
              {customers.length === 0 ? "No customers found" : "Select a customer"}
            </p>
            <p className="empty-sub">
              {customers.length === 0
                ? `No orders by ${selectedPhotographer.label} at ${selectedHospital.label}.`
                : `${customers.length} customer${customers.length !== 1 ? "s" : ""} · orders by ${selectedPhotographer.label} at ${selectedHospital.label}.`}
            </p>
          </div>
        )}

        {/* ── Photo Grid (all photos grouped by folder) ── */}
        {selectedCustomer && order?.OrderID && photos.length > 0 && (
          <section className="photos-section">
            <div className="photos-header">
              <h2 className="photos-heading">
                PHOTOS ({photos.length}{required > 0 ? ` OF ${required}` : ""})
              </h2>
              <div className="legend">
                <span className="legend-dot legend-dot--existing" />
                <span>Uploaded</span>
                <span className="legend-dot legend-dot--new" style={{ marginLeft:12 }} />
                <span>New</span>
              </div>
            </div>

            {/* Each folder shows its own uploaded + new photos together */}
            {FOLDERS.map(folder => {
              const folderPhotos = photos.filter(p => p.folderType === folder.key);
              const uploadedInFolder = folderPhotos.filter(p => p.isExisting).length;
              const newInFolder      = folderPhotos.filter(p => !p.isExisting).length;
              if (folderPhotos.length === 0) return null;
              return (
                <div key={folder.key} className="folder-section">
                  <div className="folder-section-header">
                    <Icon name="folder" size={14} color={folder.color} />
                    <span className="folder-section-title" style={{ color: folder.color }}>
                      {folder.label}
                    </span>
                    <span className="folder-section-meta">
                      {uploadedInFolder > 0 && <span>{uploadedInFolder} uploaded</span>}
                      {uploadedInFolder > 0 && newInFolder > 0 && <span style={{ opacity:0.4 }}> · </span>}
                      {newInFolder > 0 && <span style={{ color: folder.color, fontWeight:700 }}>{newInFolder} new</span>}
                    </span>
                    <button
                      className="folder-section-add"
                      style={{ color: folder.color, borderColor: folder.border }}
                      onClick={() => { setSelectedFolder(folder.key); setSubmitted(false); fileRef.current?.click(); }}
                    >
                      + Add
                    </button>
                  </div>
                  <div className="photo-grid">
                    {folderPhotos.map((photo, index) => (
                      <div key={photo.id}
                        className={`photo-card${photo.isExisting ? " photo-card--existing" : ""}`}
                        style={{ background: photo.preview ? "none" : photo.color, cursor:"pointer" }}
                        onClick={() => openLightbox(photo)}
                      >
                        {photo.preview
                          ? <img src={photo.preview} alt={photo.name} className="photo-preview-img" />
                          : <span style={{ display:"flex", alignItems:"center", justifyContent:"center", width:"100%", height:"100%" }}><Icon name="image" size={32} color="rgba(0,0,0,0.25)" strokeWidth={1.5} /></span>
                        }
                        <div style={{ position:"absolute", inset:0, background:"rgba(0,0,0,0)", display:"flex", alignItems:"center", justifyContent:"center", borderRadius:"inherit", transition:"background 0.2s" }} className="photo-hover-overlay">
                          <span style={{ opacity:0, transition:"opacity 0.2s" }} className="photo-hover-icon"><Icon name="search" size={22} color="#fff" strokeWidth={2} /></span>
                        </div>
                        {photo.isExisting
                          ? <span className="existing-badge">Uploaded</span>
                          : <span className="new-badge">New</span>
                        }
                        <button className="photo-remove" onClick={(e) => removePhoto(e, photo.id)}><Icon name="x" size={11} color="#fff" strokeWidth={2.5} /></button>
                        <span className="photo-num">#{String(index + 1).padStart(2, "0")}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}

            {/* Add more card */}
            <div className="photo-grid" style={{ marginTop:8 }}>
              <div className="photo-card photo-card--add" onClick={() => fileRef.current?.click()}>
                <Icon name="plus" size={28} color="#94a3b8" strokeWidth={1.8} />
              </div>
            </div>
          </section>
        )}

        {/* ── Info Cards ── */}
        {selectedCustomer && (
          <div className="info-cards">
            <div className="info-card">
              <div className="info-card-label">PACKAGE</div>
              <div className="info-card-value">{packageInfo?.PackageName || "—"}</div>
              <div className="info-card-sub">{packageInfo?.PackagePrice ? `₹${packageInfo.PackagePrice}` : "No order"}</div>
            </div>
            <div className="info-card">
              <div className="info-card-label">REQUIRED PHOTOS</div>
              <div className="info-card-value">{required || "—"}</div>
              <div className={`info-card-sub ${photos.length >= required && required > 0 ? "text-green" : ""}`}>
                {required > 0 ? photos.length >= required ? "Completed ✓" : `${required - photos.length} more needed` : "No limit set"}
              </div>
            </div>
            <div className="info-card">
              <div className="info-card-label">COPIES / FRAME</div>
              <div className="info-card-value">{order?.NoOfCopies || "—"}</div>
              <div className="info-card-sub">Frame: {order?.FrameSize || "—"}</div>
            </div>
            <div className="info-card">
              <div className="info-card-label">ORDER NO</div>
              <div className="info-card-value">{order?.OrderNo || "—"}</div>
              <div className="info-card-sub">{order?.OrderStatus || "No order"}</div>
            </div>
          </div>
        )}

        {/* ── Upload Progress Overlay ── */}
        {uploading && (
          <>
            <style>{`
              @keyframes uploadBounce { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-6px)} }
              @keyframes shimmer { 0%{transform:translateX(-100%)} 100%{transform:translateX(400%)} }
              @keyframes spinRing { to{transform:rotate(360deg)} }
              @keyframes fadeInScale { from{opacity:0;transform:scale(0.92)} to{opacity:1;transform:scale(1)} }
            `}</style>
            <div style={{
              position:"fixed", inset:0, zIndex:9998,
              background:"rgba(15,23,42,0.6)", backdropFilter:"blur(6px)",
              display:"flex", alignItems:"center", justifyContent:"center",
            }}>
              <div style={{
                background:"#fff", borderRadius:24, padding:"40px 44px",
                width:"min(460px,90vw)", boxShadow:"0 32px 80px rgba(0,0,0,0.28)",
                animation:"fadeInScale 0.25s ease",
              }}>

                {/* Icon */}
                <div style={{ textAlign:"center", marginBottom:24 }}>
                  <div style={{
                    width:72, height:72, borderRadius:"50%", margin:"0 auto 0",
                    background:"linear-gradient(135deg,#3b82f6,#6366f1)",
                    display:"flex", alignItems:"center", justifyContent:"center",
                    boxShadow:"0 8px 24px rgba(99,102,241,0.35)",
                    animation:"uploadBounce 1.8s ease-in-out infinite",
                  }}>
                    <Icon name="upload" size={30} color="#fff" strokeWidth={2} />
                  </div>
                </div>

                {/* Title + subtitle */}
                <div style={{ textAlign:"center", marginBottom:28 }}>
                  <h2 style={{ fontSize:20, fontWeight:700, color:"#111827", margin:"0 0 6px" }}>
                    {uploadPhase === "saving" ? "Saving to Google Drive" : "Uploading Photos"}
                  </h2>
                  <p style={{ fontSize:13, color:"#6b7280", margin:0 }}>
                    {uploadPhase === "saving"
                      ? "Organizing your photos in Drive…"
                      : <><strong style={{color:"#111827"}}>{uploadCount} photo{uploadCount !== 1 ? "s" : ""}</strong> · <strong style={{color:"#111827"}}>{activeFolderLabel}</strong> · ~{totalSizeMB} MB</>
                    }
                  </p>
                </div>

                {/* Big percent / spinner */}
                <div style={{ textAlign:"center", marginBottom:16, height:64, display:"flex", alignItems:"center", justifyContent:"center" }}>
                  {uploadPhase === "saving" ? (
                    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:8 }}>
                      <div style={{
                        width:40, height:40,
                        border:"4px solid #e0e7ff",
                        borderTopColor:"#6366f1",
                        borderRadius:"50%",
                        animation:"spinRing 0.8s linear infinite",
                      }} />
                      <span style={{ fontSize:12, color:"#6b7280", fontWeight:500 }}>Processing on server…</span>
                    </div>
                  ) : (
                    <>
                      <span style={{
                        fontSize:52, fontWeight:800, letterSpacing:"-2px",
                        background:"linear-gradient(135deg,#3b82f6,#6366f1)",
                        WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent",
                      }}>
                        {uploadProgress}
                      </span>
                      <span style={{ fontSize:24, fontWeight:700, color:"#9ca3af" }}>%</span>
                    </>
                  )}
                </div>

                {/* Progress bar */}
                <div style={{ height:10, background:"#f1f5f9", borderRadius:99, overflow:"hidden", marginBottom:24, position:"relative" }}>
                  {uploadPhase === "saving" ? (
                    <div style={{ position:"relative", height:"100%", overflow:"hidden", borderRadius:99, background:"#e0e7ff" }}>
                      <div style={{
                        position:"absolute", height:"100%", width:"40%",
                        background:"linear-gradient(90deg,#3b82f6,#6366f1)",
                        borderRadius:99,
                        animation:"shimmer 1.4s ease-in-out infinite",
                      }} />
                    </div>
                  ) : (
                    <div style={{
                      height:"100%", borderRadius:99,
                      background:"linear-gradient(90deg,#3b82f6,#6366f1)",
                      width:`${uploadProgress}%`,
                      transition:"width 0.4s ease",
                      position:"relative", overflow:"hidden",
                    }}>
                      <div style={{
                        position:"absolute", inset:0,
                        background:"linear-gradient(90deg,transparent,rgba(255,255,255,0.35),transparent)",
                        animation:"shimmer 1.4s ease-in-out infinite",
                      }} />
                    </div>
                  )}
                </div>

                {/* Phase steps */}
                <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:0 }}>
                  {[
                    { label:"Sending",    done: uploadPhase === "saving", active: uploadPhase === "sending" },
                    { label:"Processing", done: false,                    active: uploadPhase === "saving"  },
                    { label:"Complete",   done: false,                    active: false                     },
                  ].map((step, i, arr) => (
                    <div key={i} style={{ display:"flex", alignItems:"center" }}>
                      <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:5 }}>
                        <div style={{
                          width:28, height:28, borderRadius:"50%",
                          background: step.done ? "linear-gradient(135deg,#10b981,#059669)"
                            : step.active ? "linear-gradient(135deg,#3b82f6,#6366f1)"
                            : "#f1f5f9",
                          display:"flex", alignItems:"center", justifyContent:"center",
                          boxShadow: step.active ? "0 0 0 4px rgba(99,102,241,0.2)" : "none",
                          transition:"all 0.3s",
                        }}>
                          {step.done
                            ? <Icon name="check" size={13} color="#fff" strokeWidth={3} />
                            : step.active
                              ? <div style={{ width:10,height:10,border:"2px solid #fff",borderTopColor:"transparent",borderRadius:"50%",animation:"spinRing 0.7s linear infinite" }} />
                              : <div style={{ width:7,height:7,background:"#cbd5e1",borderRadius:"50%" }} />
                          }
                        </div>
                        <span style={{ fontSize:10, fontWeight:600, whiteSpace:"nowrap",
                          color: step.done ? "#10b981" : step.active ? "#6366f1" : "#9ca3af" }}>
                          {step.label}
                        </span>
                      </div>
                      {i < arr.length - 1 && (
                        <div style={{
                          width:44, height:2, margin:"0 4px", marginBottom:16,
                          background: step.done ? "#10b981" : "#e2e8f0",
                          transition:"background 0.3s",
                        }} />
                      )}
                    </div>
                  ))}
                </div>

                <p style={{ textAlign:"center", fontSize:11, color:"#9ca3af", marginTop:20, marginBottom:0 }}>
                  Please don't close this tab
                </p>
              </div>
            </div>
          </>
        )}

        {/* ── Footer Bar ── */}
        {selectedCustomer && order?.OrderID && (selectedFolderPhotos.length > 0 || photos.length > 0) && (
          <div className="footer-bar">
            <span className="footer-info">
              <strong>{activeFolderLabel}</strong>:{" "}
              <strong>{selectedFolderPhotos.length}</strong> photo{selectedFolderPhotos.length !== 1 ? "s" : ""} ready ·{" "}
              <strong>~{totalSizeMB} MB</strong> · {order.OrderNo}
            </span>
            <div className="footer-actions">
              {selectedFolderPhotos.length > 0 && (
                <button className="btn-clear" onClick={() => {
                  const count = selectedFolderPhotos.length;
                  setPhotos(prev => prev.filter(p => p.isExisting || p.folderType !== selectedFolder));
                  setSubmitted(false);
                  addToast({ type:"info", title:`${count} photo${count !== 1 ? "s" : ""} cleared from ${activeFolderLabel}` });
                }}>
                  Clear {activeFolderLabel}
                </button>
              )}
              <button
                className={`btn-submit ${isReady && !uploading ? "btn-submit--ready" : "btn-submit--disabled"}`}
                onClick={handleUpload}
                disabled={!isReady || uploading}
                style={{ display:"flex", alignItems:"center", gap:7 }}
              >
                {submitted
                  ? <><Icon name="check" size={16} color="#fff" strokeWidth={2.5} /> Uploaded!</>
                  : <><Icon name="upload" size={16} color="#fff" /> Upload {selectedFolderPhotos.length} to {activeFolderLabel}</>
                }
              </button>
            </div>
          </div>
        )}

      </main>
    </div>
  );
}